import { connectUtil, PropsFromRedux } from 'base/src/utils/reduxUtil';
import { RootStateCampanha } from 'campanha/src/store/campanhaReducer';
import * as S from './gerarLoteStyle';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CampanhaParameters } from '../../infra/routes/campanhaRoutes';
import ViewContainer from '../viewContainer/viewContainer';
import { TextField } from '@mui/material';
import { ObjectControl } from 'domain/utils/ReactUtils';
import { CampanhaPedidoResgateResponse } from 'domain/types/_carteira/campanhaPedidoResgate/campanhaPedidoResgateResponse';
import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CreateLote } from '../../store/pedidoLotes/actions/pedidoLotesAction';
import { LotePedidoRequest } from '../../types/lotePedido/lotePedidoRequest';
import { useGeolocation } from '../../hooks/useGeolocation';
import { _UserData, UserDataBaseEnum } from 'base/src/infra/storage/localStorage';
import { Button } from 'base/src/components/Button';
import PagedGerarLote from '../pagarPedido/components/pagedGerarLote/pagedGerarLote';
import { PageBanner } from 'base/src/components/PageBanner';

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    pagarPedido: state.PedidoLotesReducer.PagarPedido,
  }),
  { CreateLote }
);

export interface GerarLoteProps extends PropsFromRedux<typeof connector> { }

function GerarLote(props: GerarLoteProps) {
  const navigate = useNavigate();
  const initialState = {} as CampanhaPedidoResgateResponse;
  const [nomeLote, setNomeLote] = useState<string>(`Lote ${new Date().toLocaleString()}`);
  const [numeroLote, setNumeroLote] = useState<string>('');

  const [request, setRequest] = useState<CampanhaPedidoResgateResponse>(initialState as CampanhaPedidoResgateResponse);

  // Usar useRef para manter uma instância estável do ObjectControl
  // mas atualizar suas propriedades state e setState quando necessário
  const controlRef = useRef<ObjectControl<CampanhaPedidoResgateResponse>>();

  if (!controlRef.current) {
    controlRef.current = new ObjectControl(request, setRequest, initialState);
  } else {
    // Atualizar as propriedades do control com os valores atuais
    controlRef.current.state = request;
    controlRef.current.setState = setRequest;
  }

  const control = controlRef.current;

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const campanhaId = searchParams.get(CampanhaParameters.campanhaId);

  const geolocation = useGeolocation();

  function handleNomeLoteChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNomeLote(e.target.value);
  }

  function handleNumeroLoteChange(e: React.ChangeEvent<HTMLInputElement>) {
    setNumeroLote(e.target.value);
  }

  // Calcula o valor total dos itens selecionados em tempo real
  const valorTotal = Array.isArray(control.state.Premiacao)
    ? control.state.Premiacao.reduce((total: number, item: CampanhaPedidoResgateResponse) => {
      return total + (Number(item.ValorTotal) || 0);
    }, 0)
    : 0;

  async function getSelectedValues() {
    const selectedItems = control.state.Premiacao || [];

    if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
      toast.warning('Nenhum item selecionado');
      return;
    }

    return {
      selectedItems,
      selectedIds: selectedItems.map((item: CampanhaPedidoResgateResponse) => item.CampanhaPedidoResgateID),
      count: selectedItems.length
    };
  }

  async function getUserIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return '';
    }
  }

  async function handleGerarLote() {
    const selectedData = await getSelectedValues();
    const userIP = await getUserIP();

    if (!selectedData) {
      return;
    }

    if (!nomeLote.trim()) {
      toast.warning('Nome do lote é obrigatório');
      return;
    }

    const { selectedIds, count } = selectedData;

    // Criar o request para o lote
    const lotePedidoRequest: LotePedidoRequest = {
      CampanhaID: Number(campanhaId),
      Latitude: geolocation.latitude,
      Longitude: geolocation.longitude,
      IP: userIP,
      RealizadoPor: _UserData.Get<string>(UserDataBaseEnum.UsuarioAcesso),
      RealizadoEm: new Date(),
      QtdeUsuarios: count,
      ValorTotal: valorTotal,
      ValorTaxa: 0,
      NumeroLote: numeroLote,
      Nome: nomeLote,
      CampanhaPedidoResgatePagamentoStatusID: 1,
      CampanhaPedidoResgateIds: selectedIds
    };

    props.CreateLote(lotePedidoRequest, t);

    toast.success(`Lote criado com ${count} itens selecionados`);

    // Navigate back to the previous page
    navigate(-1);
  }

  function handleCancel() {
    navigate(-1);
  }

  return (
    <ViewContainer >
      <S.Container>
        <S.Header>
          <PageBanner showGoBack={false} title='Criar Lote' subtile='Gerar Novo Lote de Pagamentos' />
        </S.Header>

        <S.ScrollableContent>
          <S.FormContainer>
            <S.FormRow>
              <S.FormField>
                <TextField
                  label="Nome do Lote *"
                  placeholder="Digite o nome do lote"
                  value={nomeLote}
                  onChange={handleNomeLoteChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </S.FormField>

              <S.FormField>
                <TextField
                  label="Número do Lote"
                  placeholder="Digite o número do lote"
                  value={numeroLote}
                  onChange={handleNumeroLoteChange}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
              </S.FormField>
            </S.FormRow>
          </S.FormContainer>

          <PagedGerarLote
            control={control}
            take={5}
            campanhaId={Number(campanhaId)}
          />
        </S.ScrollableContent>

        <S.FixedFooter>
          <S.SummaryContainer>
            <span>Itens selecionados: {Array.isArray(control.state.Premiacao) ? control.state.Premiacao.length : 0}</span>
            <span>Valor Total: R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </S.SummaryContainer>
          <S.ButtonGroup>
            <Button full size='xsm' variant='secondary' onClick={handleCancel}>
              Cancelar
            </Button>
            <Button full size='xsm' variant='success' onClick={handleGerarLote}>
              Gerar Lote
            </Button>
          </S.ButtonGroup>
        </S.FixedFooter>
      </S.Container>
    </ViewContainer>
  );
}

export default connector(GerarLote);
